// currentUser.js
//
// Resolves the currently authenticated user's NIK so callers can gate
// presentation-only behaviour (e.g. the compact layout) on a specific user.
//
// How NIK is resolved in this app:
//   - The app stores an auth token in localStorage under the key `token`
//     via react-use's `useLocalStorage('token', '')`. react-use serialises
//     values with JSON.stringify, so the stored value is a JSON-encoded
//     string (wrapped in quotes). `getStoredToken()` recovers the raw token.
//   - There is no JWT-decode dependency in the project and the token format
//     is opaque, so we cannot assume the NIK is a JWT claim. As a best-effort
//     fast path we still attempt a dependency-free JWT payload decode in case
//     the token happens to be a JWT carrying the NIK; otherwise we fall back
//     to the same `/dg_get_user` endpoint the rest of the app already uses
//     (see src/helpers/api/users.js and projectReportServices.getUserByToken).
//     The resolved user object exposes the NIK on a `nik` field (as seen in
//     CreateProjectModal: `UserNikCreated: userData?.nik`).
//
// If the NIK genuinely cannot be resolved, `getCurrentNik()` resolves to
// `null`. Callers MUST treat `null` as "not the authorized user" and render
// the baseline layout. This helper never assumes a layout — it only reports
// the NIK (or null).

import { getUser } from './api/users';

// The single NIK permitted to see the compact layout.
export const AUTHORIZED_NIK = '3157181219';

// react-use stores localStorage values JSON-serialised. Recover the raw token.
export const getStoredToken = () => {
  try {
    const raw = localStorage.getItem('token');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') return parsed || null;
      return parsed != null ? parsed : null;
    } catch {
      // Not JSON-encoded; use the raw value as-is.
      return raw;
    }
  } catch {
    return null;
  }
};

// Best-effort, dependency-free decode of a JWT payload. Returns the claims
// object, or null if the token is not a well-formed JWT.
const decodeJwtPayload = (token) => {
  try {
    if (typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// Pull a NIK value out of an arbitrary object, tolerating common key casings.
const extractNik = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  const val = obj.nik ?? obj.NIK ?? obj.Nik;
  if (val == null) return null;
  const str = String(val).trim();
  return str.length ? str : null;
};

// Resolve the current user's NIK. Returns a string, or null when unavailable.
export const getCurrentNik = async () => {
  const token = getStoredToken();
  if (!token) return null;

  // Fast path: token might itself be a JWT carrying the NIK claim.
  const claimNik = extractNik(decodeJwtPayload(token));
  if (claimNik) return claimNik;

  // Fallback: resolve the user via the existing user endpoint.
  try {
    const response = await getUser(token);
    if (!response || !response.ok) return null;
    const body = await response.json();
    if (body && body.status === true) {
      return extractNik(body.data);
    }
  } catch {
    // Network/parse failure — treat as unavailable (baseline layout).
  }
  return null;
};

// React hook: resolves whether the current user is authorized for the compact
// layout. Starts as `false` (baseline) and flips to `true` only after the NIK
// resolves and matches AUTHORIZED_NIK. On any failure it stays `false`.
import { useEffect, useState } from 'react';

export const useIsCompactUser = () => {
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    let active = true;
    getCurrentNik()
      .then((nik) => {
        if (active) setCompact(nik === AUTHORIZED_NIK);
      })
      .catch(() => {
        if (active) setCompact(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return compact;
};
